import React from "react";
import styled from "@emotion/styled";
import theme from "@utils/theme";
import { Edit2, Printer } from "lucide-react";
import { useModal } from "@context/ModalContext";
import { useNavigate } from "react-router-dom";
import { Helper } from "@utils/index";
import Invoice from "../../invoice";
import RouteMap from "./routeMap";
import EditOrderModal from "../../edit-order/EditOrderModal";
import { Order } from "@apis/index";
import { toast } from "react-toastify";

const REJECTION_REASONS: Record<string, string> = {
  "1": "Out of Stock",
  "2": "Store Closed",
  "3": "Delivery Unavailable",
  "4": "Item Discontinued",
  "5": "Other",
};

/* ---------------- STYLES ---------------- */

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 30px;
  flex-wrap: wrap;

  border-radius: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const OrderId = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #222;
`;

const OrderDate = styled.p`
  font-size: 14px;
  color: #555;
`;

const StoreName = styled.p`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const OutlineButton = styled.button`
  margin-top: 10px;
  padding: 8px 14px;
  border: 1px solid ${theme.orange.orange200};
  border-radius: 6px;
  background: transparent;
  color: ${theme.orange.orange200};
  font-weight: 500;
  cursor: pointer;
  width: fit-content;
  transition: 0.2s;

  &:hover {
    background: ${theme.orange.orange400};
    color: #fff;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
  gap: 12px;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 4px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    background: #e6e6e6;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 6px;
  font-size: 14px;
`;

const Label = styled.p`
  color: #555;
  font-weight: 500;
  font-size: 12px;
`;

const Value = styled.p<{ status?: string }>`
  color: ${({ status }) =>
    status === "pending"
      ? "#e67e22"
      : status === "delivered"
        ? "#27ae60"
        : "#222"};
  font-weight: 400;
  text-transform: capitalize;
  font-size: 12px;
`;

const PaymentStatus = styled(Value)<{ unpaid?: boolean }>`
  background: ${({ unpaid }) => (unpaid ? "#faeae6" : "#e6f9f0")};
  color: ${({ unpaid }) => (unpaid ? "#c93103" : "#1e8a43")};
  border-radius: 8px;
  padding: 2px 8px;
`;

const ModuleTag = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 12px;
  width: fit-content;
`;

const TrackingId = styled.p`
  font-size: 13px;
  color: #666;
  font-family: monospace;
`;
const TimerBadge = styled.div<{ warning?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: ${({ warning }) => (warning ? "#fff1f2" : "#f0fdf4")};
  border: 1px solid ${({ warning }) => (warning ? "#fda4af" : "#86efac")};
  border-radius: 12px;
  margin-top: 10px;
  width: 100%;

  p {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${({ warning }) => (warning ? "#e11d48" : "#16a34a")};
  }

  span {
    font-size: 24px;
    font-weight: 700;
    color: ${({ warning }) => (warning ? "#e11d48" : "#16a34a")};
    font-family: "Courier New", Courier, monospace;
  }
`;
/* ---------------- COMPONENT ---------------- */

const OrderInfo = ({
  order,
  refreshOrder,
}: {
  order: any;
  refreshOrder: () => void;
}) => {
  const user = Helper.getUser();
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const [timeLeft, setTimeLeft] = React.useState<number>(order?.remainingSeconds || 0);

  // Synchronize timer when order prop changes
  React.useEffect(() => {
    setTimeLeft(order?.remainingSeconds || 0);
  }, [order?.remainingSeconds]);

  // Countdown logic
  React.useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Order items/amount can only be edited while the order is still awaiting
  // store confirmation. Once accepted, quantities/totals are locked.
  const editableStatuses = ["Order Placed"];
  const isEditable = editableStatuses.includes(order?.status);

  // Specific pharmacy actions
  const isPharma = order?.module?.type?.toLowerCase() === "pharma";
  const hasTimer = ["Prescription Requested", "Viewing Order", "Prescription Accepted", "Creating Invoice"].includes(order?.status);
  const isExpired = hasTimer && (timeLeft <= 0 || order?.remainingSeconds === 0 || order?.remainingSeconds === undefined);

  const canAcceptPrescription =
    isPharma && order?.status === "Prescription Requested" && !isExpired;
  const canSendInvoice =
    isPharma &&
    (order?.status === "Prescription Accepted" ||
      order?.status === "Prescription Requested") && !isExpired;

  // Get store info from first product
  const store = order?.items?.[0]?.product?.store || {};

  // Get payment info
  const transaction = order?.transaction || {};
  const paymentMethod =
    transaction.method?.toUpperCase() === "COD"
      ? "Cash On Delivery"
      : transaction.method || "N/A";
  const isPaid =
    transaction.status === "Paid" || transaction.status === "SUCCESS";

  // Extract locations
  const storeLocation =
    store.lat && store.lng
      ? { lat: Number(store.lat), lng: Number(store.lng) }
      : null;

  const shippingInfo = order?.shippingInfos || {};
  const customerLocation =
    shippingInfo.latitude && shippingInfo.longitude
      ? {
          lat: Number(shippingInfo.latitude),
          lng: Number(shippingInfo.longitude),
        }
      : null;

  // Track viewing event if it's a global prescription order and hasn't been viewed yet
  // Viewing tracking moved to parent OrderDetails component to ensure proper synchronization

  const handleShowLocation = () => {
    if (!storeLocation && !customerLocation) {
      alert("No location data available for store or customer.");
      return;
    }

    showModal(
      <div style={{ padding: "20px", background: "#fff", borderRadius: "8px" }}>
        <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600 }}>
          Route: Store to Customer
        </h3>
        <RouteMap origin={storeLocation} destination={customerLocation} />
      </div>,
      { width: "800px" },
    );
  };

  return (
    <Container>
      {/* LEFT SECTION */}
      <LeftSection>
        <OrderId>Order #{order?.id}</OrderId>
        <OrderDate>{new Date(order?.createdAt).toLocaleString()}</OrderDate>
        <StoreName>Store: {store.name || order?.storeId || "N/A"}</StoreName>
        {order?.module?.name && <ModuleTag>{order.module.name}</ModuleTag>}
        {order?.trackingId && (
          <TrackingId>Tracking: {order.trackingId}</TrackingId>
        )}

        {/* Timer removed from here as per user request to avoid duplicate and redundant view timer */}

        <OutlineButton onClick={handleShowLocation}>
          Show Location on Map
        </OutlineButton>
      </LeftSection>

      {/* RIGHT SECTION */}
      <RightSection>
        <ActionButtons>
          {canAcceptPrescription && (
            <>
              <ActionButton
                style={{
                  background: "#ecfdf5",
                  color: "#059669",
                  borderColor: "#10b981",
                }}
                onClick={async () => {
                  try {
                    await Order.updateStatus(order.id, "Prescription Accepted");
                    toast.success("Prescription accepted");
                    refreshOrder();
                  } catch (err: any) {
                    toast.error(err.message || "Failed to accept prescription");
                  }
                }}
              >
                Accept Prescription
              </ActionButton>
              <ActionButton
                style={{
                  background: "#fef2f2",
                  color: "#dc2626",
                  borderColor: "#f87171",
                }}
                onClick={async () => {
                  try {
                    await Order.updateStatus(order.id, "Prescription Rejected");
                    toast.success("Prescription rejected");
                    refreshOrder();
                  } catch (err: any) {
                    toast.error(err.message || "Failed to reject prescription");
                  }
                }}
              >
                Reject Prescription
              </ActionButton>
            </>
          )}

          {isEditable && (canSendInvoice || !isPharma) && (
            <ActionButton
              onClick={() => {
                showModal(
                  <EditOrderModal order={order} onSuccess={refreshOrder} />,
                  { width: "95%" },
                );
              }}
            >
              <Edit2 width={13} />
              <p>{isPharma ? "Create Invoice" : "Edit"}</p>
            </ActionButton>
          )}
          <ActionButton
            onClick={() => {
              showModal(<Invoice order={order} />, { width: "480px" });
            }}
          >
            <Printer width={13} />
            <p>Print Invoice</p>
          </ActionButton>
        </ActionButtons>

        <InfoList>
          <InfoRow>
            <Label>Status:</Label>
            <Value status={order?.status?.toLowerCase()}>{order?.status}</Value>
          </InfoRow>

          {order?.status === "Rejected" && (
            <>
              <InfoRow>
                <Label>Rejection Reason:</Label>
                <Value style={{ color: "#ef4444" }}>
                  {REJECTION_REASONS[String(order?.rejectionReasonId)] || "N/A"}
                </Value>
              </InfoRow>
              {order?.rejectionNote && (
                <InfoRow>
                  <Label>Rejection Note:</Label>
                  <Value style={{ color: "#ef4444" }}>
                    {order.rejectionNote}
                  </Value>
                </InfoRow>
              )}
            </>
          )}

          <InfoRow>
            <Label>Payment Method:</Label>
            <Value>{paymentMethod}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Payment Status:</Label>
            <PaymentStatus unpaid={!isPaid}>
              {isPaid ? "Paid" : "Unpaid"}
            </PaymentStatus>
          </InfoRow>
          <InfoRow>
            <Label>Order Type:</Label>
            <Value>{order?.shippingInfos?.type || "Delivery"}</Value>
          </InfoRow>
        </InfoList>
      </RightSection>
    </Container>
  );
};

export default OrderInfo;
